import { useTranslation } from 'react-i18next';

import useBrandName from '@/hooks/use-brand-name';

import styles from './index.module.scss';

/**
 * TDA: the product wordmark.
 *
 * NEW FILE — upstream has no equivalent, so it costs nothing at merge time.
 * Lifted verbatim out of `Layout/AppLayout/TopBar`: the landing screens now
 * carry the brand inside the card while the secondary screens keep it in the
 * page header, so two places need the same markup and the same accent rules.
 *
 * ⚠️ Split into real spans rather than done with CSS, so a screen reader gets
 * one uninterrupted word instead of three fragments.
 *
 * ⚠️ The name comes from a PHRASE, not a literal. `custom_phrases` is per
 * TENANT, so the admin tenant can say "TDxIdentity Console" while customers
 * keep "TDxIdentity" — the same mechanism already used for the line under the
 * headline. Without it, an operator signing in to the system console sees
 * exactly the screen a customer sees, which is the one moment it matters.
 *
 * ⚠️ The `x` accent is derived, not hard-coded. Splitting on the literal "TDx"
 * keeps the styled letter working for any name that starts with it, and
 * degrades to plain text for one that does not — a tenant is free to rename
 * this entirely.
 *
 * ⚠️ The SIZE lives in this component's own stylesheet, NOT at the call sites.
 * It started as a `className` each caller passed, on the theory that a page
 * header and a card header want different scales. They immediately disagreed —
 * 20px/700 in the bar against 28px/bold in the card — and one product read as
 * two marks. The logo height went the same way: a token in
 * `shared/scss/normalized.scss` that both copies read.
 */
const Wordmark = () => {
  const { t } = useTranslation();
  // The phrase's qualifier (e.g. "Console") is kept whichever brand name is used.
  const brandName = useBrandName();
  // The phrase ships as "TDxIdentity"; the brand name takes its place.
  const name = t('description.tdx_product_name').replace(/^TDxIdentity/, brandName);
  const marker = 'TDx';

  if (!name.startsWith(marker)) {
    return <span className={styles.wordmark}>{name}</span>;
  }

  /*
   * ⚠️ Split on the FIRST space, not on the literal "Console".
   *
   * "TDxIdentity" is the product; anything after it — "Console" here — is a
   * qualifier saying which surface you are on. Drawing them at the same weight
   * made the admin screen read as a different PRODUCT rather than a different
   * door into the same one. Splitting on the space keeps that true for whatever
   * a tenant renames this to.
   */
  const [brand = name, ...restWords] = name.split(' ');
  const qualifier = restWords.join(' ');

  return (
    <span className={styles.wordmark}>
      TD<span className={styles.accent}>x</span>
      {brand.slice(marker.length)}
      {/* ⚠️ A real space, not just the margin. Spacing by CSS alone renders
          correctly but makes the accessible name "TDxIdentityConsole" — one
          run-on word to a screen reader, and to anyone who copies the text. */}
      {qualifier && <span className={styles.qualifier}> {qualifier}</span>}
    </span>
  );
};

export default Wordmark;
